import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react';
import Animation from '../Animation';
import FormBuilderBulkActionsView from '../view/FormBuilderBulkActionsView';
import { ACTIVE_VIEW } from '../engine/FormBuilderAutomationsEngine';
import BulkActionWizardEngine from './BulkActionWizardEngine';
import SerializationCopy from '../../utils/SerializationCopy';
import { FormBuilderContext } from './FormBuilderEngine';
import useSkipUpdate from '../../hooks/useSkipRender';
import { UPDATE_FORM } from '../../store/modules/state/formBuilder';
import { replaceElements } from './FormBuilderStatusesEngine';

export const BulkActionsContext = createContext({});

const BulkActionsReducer = (state, action) => {
  const { type, payload, id } = action;
  let index, bulkActionsCopy;

  switch (type) {
    case 'update':
      return { ...state, ...payload };
    case 'update_single_bulk_action':
      index = state.bulkActions.findIndex(a => a.id === id);
      bulkActionsCopy = JSON.parse(JSON.stringify(state.bulkActions));
      bulkActionsCopy[index] = payload;
      return { ...state, bulkActions: bulkActionsCopy };
    default:
      return state;
  }
};

const initialReducerState = {
  bulkActions: []
};

const sortBulkActionsByPosition = (a, b) => {
  if (a.position === undefined || !b.position === undefined) {
    return 0;
  }

  return a.position - b.position;
};

const FormBuilderBulkActionsEngine = ({}) => {
  const { form, reduxDispatch } = useContext(FormBuilderContext);
  const isDataInitiated = useRef(false);
  const [data, dispatch] = useReducer(BulkActionsReducer, initialReducerState);
  const [wizardBulkActionData, setWizardBulkActionData] = useState(undefined);
  const [bulkActionToDelete, setBulkActionToDelete] = useState('');
  const [activeView, setActiveView] = useState(ACTIVE_VIEW.BULK_ACTIONS);
  const [customStatusLabels, setCustomStatusLabels] = useState({});
  const { shouldSkipUpdate, skipUpdate } = useSkipUpdate();
  const disableAddButton = data.bulkActions.length > 4;

  const initData = useCallback(async () => {
    const bulkActions = form.quickBtnsBulkActions || [];
    skipUpdate();
    dispatch({ type: 'update', payload: { bulkActions } });
  }, [form]);

  useEffect(() => {
    let newCustomStatusesObject = {};
    if (
      form &&
      form.customStatuses &&
      Object.keys(customStatusLabels).length === 0
    ) {
      form.customStatuses.forEach(status => {
        newCustomStatusesObject[status.internalId] = status.label;
      });
      setCustomStatusLabels(newCustomStatusesObject);
    }
  }, [form]);

  useEffect(() => {
    if (shouldSkipUpdate()) {
      return;
    }

    const updateFormSchemaViaAPI = () => {
      const bulkActions = data.bulkActions;
      fetchAPI(`/forms/v1/forms/${form.id}`, {
        method: 'PUT',
        body: {
          ...form,
          quickBtnsBulkActions: bulkActions
        }
      })
        .then(() => {
          // changes saved
          openSnackbar('success', 'bulkActions.snackbar.success.general');
          reduxDispatch({
            type: UPDATE_FORM,
            payload: {
              ...form,
              quickBtnsBulkActions: bulkActions
            }
          });
        })
        .catch(e => openSnackbar('error', 'bulkActions.snackbar.fail.save'));
    };

    updateFormSchemaViaAPI();
  }, [data]);

  useEffect(() => {
    if (!form || !Object.keys(form).length || isDataInitiated.current) {
      return;
    }
    isDataInitiated.current = true;
    initData();
  }, [form]);

  const clickedAddBulkAction = () => {
    setActiveView(ACTIVE_VIEW.BULK_ACTION_WIZARD);
  };

  const clickedEditBulkAction = bulkActionData => {
    setWizardBulkActionData(bulkActionData);
    setActiveView(ACTIVE_VIEW.BULK_ACTION_WIZARD);
  };

  const onWizardClosed = useCallback(view => {
    setWizardBulkActionData(undefined);
    setActiveView(view || ACTIVE_VIEW.BULK_ACTIONS);
  }, []);

  const updateSingleBulkAction = useCallback(
    bulkActionData => {
      const bulkActions = SerializationCopy(data.bulkActions);
      const index = bulkActions.findIndex(b => b.id === bulkActionData.id);

      if (index === -1) {
        bulkActionData.position = bulkActions.length;
        bulkActions.push(bulkActionData);
      } else {
        bulkActions[index] = bulkActionData;
      }

      dispatch({
        type: 'update',
        payload: {
          bulkActions
        }
      });
    },
    [data]
  );

  const onBulkActionDelete = useCallback(
    bulkActionId => {
      const bulkActions = SerializationCopy(data.bulkActions);
      const updatedBulkActions = bulkActions.filter(
        cb => cb.id !== bulkActionId
      );

      dispatch({
        type: 'update',
        payload: {
          bulkActions: updatedBulkActions
        }
      });
      setBulkActionToDelete('');
    },
    [data]
  );

  const onDragEnd = useCallback(
    result => {
      if (!result.destination) {
        return;
      }
      if (result.destination.index === result.source.index) {
        return;
      }
      const previousIndex = result.source.index;
      const newIndex = result.destination.index;
      const updatedBulkActions = replaceElements(
        data.bulkActions,
        previousIndex,
        newIndex
      ).map((ba, i) => ({ ...ba, position: i }));
      updatedBulkActions.sort(sortBulkActionsByPosition);
      dispatch({
        type: 'update',
        payload: { bulkActions: updatedBulkActions }
      });
    },
    [data]
  );

  const viewProps = {
    clickedAddBulkAction,
    clickedEditBulkAction,
    customStatusLabels,
    disableAddButton,
    list: data.bulkActions || [],
    onBulkActionDelete,
    onDragEnd
  };

  const wizardProps = {
    actionData: wizardBulkActionData,
    save: updateSingleBulkAction
  };

  const contextProps = {
    bulkActionToDelete,
    setActiveView,
    onBulkActionDelete,
    onWizardClosed
  };

  return (
    <BulkActionsContext.Provider value={contextProps}>
      <Animation
        type="collapse"
        in={activeView === ACTIVE_VIEW.BULK_ACTIONS}
        unmountOnExit
        mountOnEnter
      >
        <FormBuilderBulkActionsView {...viewProps} />
      </Animation>
      <Animation
        type="collapse"
        in={activeView === ACTIVE_VIEW.BULK_ACTION_WIZARD}
        unmountOnExit
        mountOnEnter
      >
        <BulkActionWizardEngine {...wizardProps} />
      </Animation>
    </BulkActionsContext.Provider>
  );
};

export default FormBuilderBulkActionsEngine;
